import React, { useCallback, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form, ErrorMessage, FieldArray, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Calendar, X, AlertCircle, Sun, CalendarRange, Clock, Bell, Users, Plus, Trash2 } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import { createCalenderEvent, updateCalendarEvent } from "../core/request";
import { addWEventAction, updateEventAction } from "../../../../../redux/actions/events.actions";
import { useUserMasterDataQuery } from "../core/_queries";
import CustomFormikInput from "../../../apps/components/CustomFormikInput";
import CustomFormikSelect from "../../../apps/components/CustomFormikSelect";
import CustomFormSearchSelect from "../../../apps/components/CustomFormSearchSelect";
import CustomFormDateInput from "../../../apps/components/CustomDateInput";
import { MODE_OPTIONS, REMINDER_UNITS, REPEAT_OPTIONS } from "../core/data";
import { CategoryColor, IEvent, IReminder } from "../core/type";
import {
  addDaysToStorageDate,
  addMinutesToWallClock,
  addMonthsToStorageDate,
  convertUTC,
  CustomDateFormate,
  formatToStorageDate,
  getSaCurrentDate,
  getSaCurrentTime,
  getSaNowLabel,
  getSaTodayDate,
  isoWeekdayFromStorageDate,
  parseDate,
} from "../../../../../utils/date";
import { normalizeDate } from "../core/_utility";
import { IUser } from "../../../core/_models";
import { useAuth } from "../../../auth";
import { getApiErrorMessage } from "../../../../../utils/utils";

// ==================== Types ====================
interface AddorUpdateModalProps {
  show: boolean;
  onHide: () => void;
  event: IEvent | null;
  isUpdateMode?: boolean;
  totalEvents: number;
  selectedDate?: string | Date | null;
}

interface Reminder {
  value: number;
  unit: string;
}

interface Invite {
  user_id: number;
  email: string;
}

interface EventFormValues {
  event_id?: number;
  title: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  description: string;
  category: string;
  place_or_link: string;
  mode: string;
  is_all_day: boolean;
  is_multi_day: boolean;
  repeat: "once" | "weekly" | "monthly";
  reminders: Reminder[];
  invites: Invite[];
}

interface UserOption {
  label: string;
  value: number;
  email?: string;
}

interface CategoryOption {
  label: string;
  value: string;
}

interface FieldProps {
  field: {
    name: string;
    value: string;
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => void;
    onBlur: (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => void;
  };
  meta: {
    touched: boolean;
    error?: string;
  };
  form: {
    setFieldValue: (field: string, value: unknown) => void;
  };
}

// ==================== Utility Functions ====================
const formatTimeWithoutSeconds = (time: string): string => time?.substring(0, 5) || "";
const getCurrentDate = (): string => getSaCurrentDate();
const getCurrentTime = (): string => getSaCurrentTime();
const pastDateMessage = (): string =>
  `Start date cannot be in the past. Current South Africa time is ${getSaNowLabel()}.`;
const formatDate = (date: Date): string => formatToStorageDate(date);
const toCalendarDate = (value: string | Date): Date => {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  return parseDate(value) ?? getSaTodayDate();
};
const isPastDate = (date: Date): boolean => normalizeDate(date) < getSaTodayDate();
const timeToMinutes = (time: string): number => {
  const [hour, minute] = time.slice(0, 5).split(":").map(Number);
  return (hour || 0) * 60 + (minute || 0);
};
const readRepeat = (event: IEvent): "once" | "weekly" | "monthly" => {
  if (event.occurrence_type === "monthly") {
    return "monthly";
  }
  const raw = event.occurrence_config;
  const config = typeof raw === "string"
    ? (() => {
      try {
        return JSON.parse(raw) as { repeat?: string };
      } catch {
        return null;
      }
    })()
    : raw;
  return config?.repeat === "weekly" ? "weekly" : "once";
};

// ==================== Reusable Components ====================

// 1. Formik Date Input
interface FormikDateInputProps {
  name: string;
  label?: string;
  min?: string;
  disabled?: boolean;
  onChange?: (date: string) => void;
}

const FormikDateInput: React.FC<FormikDateInputProps> = ({
  name,
  label,
  min,
  disabled,
  onChange
}) => (
  <div className="mb-2">
    {label && <label className="form-label fw-semibold mb-1 small">{label}</label>}
    <Field name={name}>
      {({ field, meta, form }: FieldProps) => (
        <>
          <CustomFormDateInput
            value={field.value}
            disabled={disabled}
            minDate={min ? parseDate(min) ?? undefined : undefined}
            onChange={(date) => {
              const next = typeof date === "string" ? date : date ? formatToStorageDate(date) : "";
              form.setFieldValue(name, next);
              onChange?.(next);
            }}
          />
          {meta.touched && meta.error ? (
            <div className="text-danger small mt-1">{meta.error}</div>
          ) : null}
        </>
      )}
    </Field>
  </div>
);

// 2. Formik Time Input
interface FormikTimeInputProps {
  name: string;
  label: string;
  min?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
}

const FormikTimeInput: React.FC<FormikTimeInputProps> = ({
  name,
  label,
  min,
  disabled,
  onChange
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div className="mb-2">
      <label className="form-label fw-semibold mb-1 d-flex align-items-center">
        <Clock size={14} className="me-1 text-primary" /> {label}
      </label>
      <Field name={name}>
        {({ field, meta, form }: FieldProps) => (
          <input
            {...field}
            ref={inputRef}
            type="time"
            className={`form-control ${meta.touched && meta.error ? "is-invalid" : ""}`}
            disabled={disabled}
            min={min || undefined}
            step="60"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const minTime = min?.slice(0, 5);
              const next = e.target.value;
              const value = minTime && next && timeToMinutes(next) < timeToMinutes(minTime) ? minTime : next;
              form.setFieldValue(name, value);
              if (inputRef.current && value !== next) {
                inputRef.current.value = value;
              }
              onChange?.(value);
            }}
          />
        )}
      </Field>
      <ErrorMessage name={name} component="div" className="text-danger small mt-1" />
    </div>
  );
};

// 3. Reminder Field Array
interface ReminderFieldArrayProps {
  name: string;
  values: Reminder[];
  disabled?: boolean;
}

const ReminderFieldArray: React.FC<ReminderFieldArrayProps> = ({
  name,
  values,
  disabled
}) => {
  const getMaxValue = (unit: string): number => {
    const limits: Record<string, number> = {
      minutes: 59,
      hours: 23,
      days: 365,
      weeks: 52,
      months: 12
    };
    return limits[unit] || 365;
  };

  return (
    <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: "#f8f9fa" }}>
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">
                <Bell size={16} className="me-1 text-primary" /> Reminders
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-light-info d-flex align-items-center gap-1"
                onClick={() => push({ value: 15, unit: "hours" })}
                disabled={disabled}
              >
                <Plus size={14} /> Add Reminder
              </button>
            </div>

            {values?.length > 0 ? (
              values.map((_, index) => (
                <div key={index} className="row g-2 mb-2 align-items-end">
                  <div className="col-md-5">
                    <Field
                      type="number"
                      name={`${name}.${index}.value`}
                      className="form-control"
                      placeholder="e.g., 15"
                      min={1}
                      max={getMaxValue(values[index]?.unit)}
                      disabled={disabled}
                    />
                    <ErrorMessage
                      name={`${name}.${index}.value`}
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                  <div className="col-md-5">
                    <Field
                      as="select"
                      name={`${name}.${index}.unit`}
                      className="form-select"
                      disabled={disabled}
                    >
                      {REMINDER_UNITS.map((unit) => (
                        <option key={unit.value} value={unit.value}>
                          {unit.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name={`${name}.${index}.unit`}
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-icon btn-light-danger w-100 d-flex align-items-center justify-content-center"
                      onClick={() => remove(index)}
                      disabled={disabled}
                      style={{ height: "38px" }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-3 text-muted">
                <Bell size={32} className="mb-2 opacity-50" />
                <p className="mb-0 small">No reminders set</p>
                <button
                  type="button"
                  className="btn btn-sm btn-link mt-2"
                  onClick={() => push({ value: 15, unit: "hours" })}
                  disabled={disabled}
                >
                  Add your first reminder
                </button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

// 4. Invite Field Array
interface InviteFieldArrayProps {
  name: string;
  userOptions: UserOption[];
  values: Invite[];
  disabled?: boolean;
  onUserSelect?: (option: UserOption | null, index: number) => void;
}

const InviteFieldArray: React.FC<InviteFieldArrayProps> = ({
  name,
  userOptions,
  values,
  disabled,
  onUserSelect
}) => {
  return (
    <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: "#f8f9fa" }}>
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">
                <Users size={16} className="me-1 text-primary" /> Invites
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-light-info d-flex align-items-center gap-1"
                onClick={() => push({ user_id: 0, email: "" })}
                disabled={disabled}
              >
                <Plus size={14} /> Add Invite
              </button>
            </div>

            {values?.length > 0 ? (
              values.map((invite, index) => (
                <div key={index}>
                  <div className="row g-2 mb-2 align-items-end">
                    <div className="col-md-5">
                      <CustomFormSearchSelect
                        name={`${name}.${index}.user_id`}
                        value={invite.user_id || null}
                        onChange={(value) => {
                          const selectedOption = userOptions.find(
                            opt => Number(opt.value) === Number(value)
                          );
                          onUserSelect?.(selectedOption || null, index);
                        }}
                        options={userOptions}
                        disabled={disabled}
                        placeholder="Search for a user..."
                        isClearable={true}
                        size="sm"
                      />
                    </div>
                    <div className="col-md-5">
                      <Field
                        type="email"
                        name={`${name}.${index}.email`}
                        className="form-control form-control-sm"
                        placeholder="e.g., john@example.com"
                        disabled
                        style={{ height: "38px" }}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-icon btn-light-danger w-100 d-flex align-items-center justify-content-center"
                        onClick={() => remove(index)}
                        disabled={disabled}
                        style={{ height: "38px" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-md-5">
                      <div className="text-danger mt-0">
                        <ErrorMessage
                          name={`${name}.${index}.user_id`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-3 text-muted">
                <Users size={32} className="mb-2 opacity-50" />
                <p className="mb-0 small">No invites sent</p>
                <button
                  type="button"
                  className="btn btn-sm btn-link mt-2"
                  onClick={() => push({ user_id: 0, email: "", fullname: "" })}
                  disabled={disabled}
                >
                  Add your first invite
                </button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

// ==================== Main Component ====================
const AddorUpdateModal: React.FC<AddorUpdateModalProps> = ({
  show,
  onHide,
  event,
  isUpdateMode = false,
  totalEvents,
  selectedDate,
}) => {
  // Hooks
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch<AppDispatch>();
  const { userData } = useUserMasterDataQuery();
  const { categoryAndColorsData } = useSelector((state: RootState) => state.eventReducer);
  const [loading, setLoading] = useState(false);

  // ==================== Memoized Values ====================
  const effectiveDate = useMemo(() => {
    if (isUpdateMode && event?.start_date) return toCalendarDate(event.start_date);
    if (selectedDate) return toCalendarDate(selectedDate);
    return getSaTodayDate();
  }, [isUpdateMode, event, selectedDate]);

  const isPast = useMemo(() => isPastDate(effectiveDate), [effectiveDate]);
  const isEditingPastEvent = useMemo(() => isUpdateMode && isPast, [isUpdateMode, isPast]);
  const minStartDate = useMemo(() => (isEditingPastEvent ? "" : getCurrentDate()), [isEditingPastEvent]);

  const { auth } = useAuth()

  const formattedDate = useMemo(
    () => CustomDateFormate(effectiveDate),
    [effectiveDate]
  );

  const categoryOptions = useMemo(
    () => categoryAndColorsData?.filter((cat: CategoryColor) => !cat.is_global).map((cat: CategoryColor) => ({
      label: cat.name,
      value: cat.name,
    })) || [],
    [categoryAndColorsData]
  );
  
  const userOptions = useMemo(
    () => userData.filter((user: IUser) => user.user_id !== auth?.db_user_id).map((user: IUser) => ({
      label: `${user.fullname} (${user.email})`,
      value: user.user_id,
      email: user.email,
    })) || [],
    [userData, auth]
  );

  // ==================== Validation Schema ====================
  const getValidationSchema = useCallback(() => {
    if (isEditingPastEvent) return Yup.object({});

    return Yup.object({
      title: Yup.string().required("Title is required"),
      start_date: Yup.string()
        .required("Start date is required")
        .test("not-past", pastDateMessage, (value) => !value || value >= getCurrentDate()),
      start_time: Yup.string().when("is_all_day", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required("Start time is required"),
      }),
      end_date: Yup.string().when(["is_all_day", "is_multi_day"], {
        is: (isAllDay: boolean, isMultiDay: boolean) => isAllDay && !isMultiDay,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.required("End date is required")
            .test("not-past", pastDateMessage, (value) => !value || value >= getCurrentDate())
            .test("is-after-or-equal", "End date must be after or equal to start date", function (value) {
              const { start_date } = this.parent;
              return !value || !start_date || value >= start_date;
            }),
      }),
      end_time: Yup.string().when("is_all_day", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.required("End time is required")
            .test("is-after", "End time must be after start time", function (value) {
              const { start_date, start_time, end_date, repeat } = this.parent;
              if (!value || !start_date || !start_time || !end_date) return true;
              if (repeat && repeat !== "once") {
                return timeToMinutes(value) !== timeToMinutes(start_time);
              }
              return `${end_date}T${value}` > `${start_date}T${start_time}`;
            }),
      }),
      category: Yup.string().required("Category is required"),
      mode: Yup.string().required("Mode is required"),
      place_or_link: Yup.string().when("mode", {
        is: "online",
        then: (schema) => schema.required("Meeting link is required").url("Enter a valid URL"),
        otherwise: (schema) => schema.required("Location is required").min(2, "Location is too short"),
      }),
      description: Yup.string(),
      is_all_day: Yup.boolean(),
      is_multi_day: Yup.boolean(),
      reminders: Yup.array().of(
        Yup.object({
          value: Yup.number()
            .required("Value is required")
            .when("unit", ([unit], schema) => {
              const limits: Record<string, [number, number]> = {
                minutes: [1, 59],
                hours: [1, 23],
                days: [1, 365],
                weeks: [1, 52],
                months: [1, 12],
              };
              const [min, max] = limits[unit] || [1, 365];
              return schema.min(min, `Must be between ${min} and ${max}`).max(max, `Must be between ${min} and ${max}`);
            }),
          unit: Yup.string().required("Unit is required"),
        })
      ),
      invites: Yup.array().of(
        Yup.object({
          user_id: Yup.number().required("User is required").min(1, "Please select a valid user"),
        })
      ),
    });
  }, [isEditingPastEvent]);

  // ==================== Initial Values ====================
  const getInitialValues = useCallback((): EventFormValues => {
    if (isUpdateMode && event) {
      const startDate = event.start_date || getCurrentDate();
      const endDate = event.end_date || startDate;
      return {
        event_id: event.event_id,
        title: event.title || "",
        start_date: startDate,
        start_time: event.start_time ? formatTimeWithoutSeconds(event.start_time) : getCurrentTime(),
        end_date: endDate,
        end_time: event.end_time ? formatTimeWithoutSeconds(event.end_time) : addMinutesToWallClock(startDate, getCurrentTime(), 60).time,
        description: event.description || "",
        category: event.category || "",
        mode: event.mode || "",
        place_or_link: event.location || "",
        is_all_day: false,
        is_multi_day: readRepeat(event) === "once" && startDate !== endDate,
        repeat: readRepeat(event),
        reminders: event.reminders || [],
        invites: event.invites || [],
      };
    }

    const startDate = selectedDate ? formatDate(toCalendarDate(selectedDate)) : getCurrentDate();
    const startTime = getCurrentTime();
    const endClock = addMinutesToWallClock(startDate, startTime, 60);

    return {
      title: "",
      start_date: startDate,
      start_time: startTime,
      end_date: endClock.date,
      end_time: endClock.time,
      description: "",
      category: "",
      place_or_link: "",
      mode: "",
      is_all_day: false,
      is_multi_day: endClock.date !== startDate,
      repeat: "once",
      reminders: [],
      invites: [],
    };
  }, [event, isUpdateMode, selectedDate]);

  // ==================== Handlers ====================
  const getMinTime = useCallback(
    (date: string) => {
      if (isEditingPastEvent) return "";
      return date === getCurrentDate() ? getCurrentTime() : "";
    },
    [isEditingPastEvent]
  );

  const getMinEndDate = useCallback(
    (startDate: string) => (isEditingPastEvent ? "" : startDate || minStartDate),
    [minStartDate, isEditingPastEvent]
  );

  const handleStartDateTimeChange = useCallback(
    (setFieldValue: (field: string, value: string | Date | boolean) => void, startDate: string, startTime: string, repeat: string) => {
      if (isEditingPastEvent || !startDate || !startTime) return;
      const endClock = addMinutesToWallClock(startDate, startTime, 60);
      if (repeat !== "once") {
        setFieldValue("end_time", endClock.time);
        return;
      }
      setFieldValue("end_time", endClock.time);
      if (endClock.date !== startDate) {
        setFieldValue("is_multi_day", true);
        setFieldValue("end_date", endClock.date);
        return;
      }
      setFieldValue("end_date", startDate);
    },
    [isEditingPastEvent]
  );

  const handleRepeatChange = useCallback(
    (repeat: "once" | "weekly" | "monthly", setFieldValue: (field: string, value: string | boolean) => void, startDate: string) => {
      setFieldValue("repeat", repeat);
      if (repeat === "once") {
        setFieldValue("is_multi_day", false);
        setFieldValue("end_date", startDate);
        return;
      }
      setFieldValue("is_multi_day", false);
      setFieldValue(
        "end_date",
        repeat === "monthly" ? addMonthsToStorageDate(startDate, 12) : addDaysToStorageDate(startDate, 52 * 7)
      );
    },
    []
  );

  const handleMultiDayToggle = useCallback(
    (isMultiDay: boolean, setFieldValue: (field: string, value: string | Date | boolean) => void, startDate: string) => {
      setFieldValue("is_multi_day", isMultiDay);
      if (isMultiDay) {
        const nextDay = parseDate(startDate);
        if (!nextDay) return;
        nextDay.setDate(nextDay.getDate() + 1);
        setFieldValue("end_date", formatDate(nextDay));
      } else {
        setFieldValue("end_date", startDate);
      }
    },
    []
  );

  const handleUserSelect = useCallback(
    (option: UserOption | null, index: number, values: EventFormValues, setFieldValue: (field: string, value: number | string) => void) => {
      if (!option) {
        setFieldValue(`invites.${index}.user_id`, 0);
        setFieldValue(`invites.${index}.email`, "");
        return;
      }

      const isDuplicate = values.invites?.some((invite: Invite, i: number) =>
        i !== index && invite.user_id === option.value
      );

      if (isDuplicate) {
        setFieldValue(`invites.${index}.user_id`, 0);
        setFieldValue(`invites.${index}.email`, "");
        return;
      }

      const selectedUser = userData?.find((user: IUser) => user.user_id === option.value);
      if (selectedUser) {
        setFieldValue(`invites.${index}.user_id`, selectedUser.user_id);
        setFieldValue(`invites.${index}.email`, selectedUser.email);
      }
    },
    [userData]
  );

  // ==================== Submit Handler ====================
  const handleSubmit = useCallback(
    async (values: EventFormValues, { setSubmitting }: FormikHelpers<EventFormValues>) => {
      // Convert reminders to match IReminder interface
      const formattedReminders: IReminder[] = values.reminders.map((r, index) => ({
        id: String(index + 1), // Convert to string
        value: r.value,
        unit: r.unit,
      }));

      const startTime = values.is_all_day ? "00:00:00" : `${values.start_time}:00`;
      const endTime = values.is_all_day ? "23:59:59" : `${values.end_time}:00`;
      const endDate = values.repeat === "once"
        ? (values.is_multi_day ? values.end_date : values.start_date)
        : values.end_date;
      const occurrenceType = values.repeat === "monthly" ? "monthly" : values.repeat === "weekly" ? "custom" : "once";
      const occurrenceDay = Number(values.start_date.slice(8, 10));
      const occurrenceConfig = values.repeat === "monthly"
        ? { day: occurrenceDay }
        : values.repeat === "weekly"
          ? { repeat: "weekly", weekday: isoWeekdayFromStorageDate(values.start_date) }
          : null;

      const payload = {
        ...values,
        event_id: isUpdateMode ? event?.event_id : undefined,
        start_time: startTime,
        end_time: endTime,
        end_date: endDate,
        start_at_utc: convertUTC(`${values.start_date}T${startTime}`),
        end_at_utc: convertUTC(`${endDate}T${endTime}`),
        occurrence_type: occurrenceType,
        occurrence_config: occurrenceConfig,
        reminders: formattedReminders,
      };

      setLoading(true);
      try {
        const response = isUpdateMode
          ? await updateCalendarEvent(payload)
          : await createCalenderEvent(payload);

        if (response.status === "ok") {
          const data = {
            ...response.data.event,
            id: response.data.event.event_id,
            location: response.data.event.place_or_link,
            occurrence_type: occurrenceType,
            occurrence_config: occurrenceConfig,
          };
          dispatch(isUpdateMode ? updateEventAction(data) : addWEventAction(data));
          showSnackbar(response.message, "success");
          onHide();
        } else {
          showSnackbar(response.message, "danger");
        }
      } catch (error) {
        showSnackbar(getApiErrorMessage(error, "Failed to save event"), "danger");
        console.error("Error saving event:", error);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
    [isUpdateMode, event, onHide, dispatch, showSnackbar, totalEvents]
  );

  // ==================== Render Helpers ====================
  const renderInfoMessage = (icon: React.ReactNode, message: string, bgColor = "#e7f3ff") => (
    <div className="mt-3 p-2 rounded-3" style={{ backgroundColor: bgColor, borderRadius: "8px" }}>
      <small className="d-flex align-items-center gap-2 text-primary">{icon} {message}</small>
    </div>
  );

  const validationSchema = useMemo(
    () => getValidationSchema(),
    [getValidationSchema]
  );

  // ==================== Render ====================
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <div className="bg-white rounded-4 shadow-lg m-3 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-bottom" style={{ backgroundColor: "#f8f9fa" }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <Calendar size={20} className="text-primary" />
              <div>
                <h5 className="mb-0 fw-semibold">
                  {isEditingPastEvent ? "View Event" : isUpdateMode ? "Edit Event" : "Create New Event"}
                </h5>
                <small className="text-muted">{formattedDate}</small>
              </div>
            </div>
            <button onClick={onHide} className="border-0 bg-transparent rounded-circle p-1" type="button">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Past Date Warning */}
        {!isUpdateMode && isPast ? (
          <div className="text-center py-5">
            <div className="alert alert-warning border-0 bg-warning bg-opacity-10 rounded-3">
              <AlertCircle className="mx-auto mb-3" size={48} style={{ color: "#f59e0b" }} />
              <h5 className="fw-semibold">Cannot create event on past dates</h5>
              <p className="mb-0 text-muted">Please select a future date to create an event.</p>
            </div>
            <button className="btn btn-primary px-4 mt-3" onClick={onHide}>Close</button>
          </div>
        ) : (
          <Formik
            key={`${isUpdateMode}-${event?.event_id}-${selectedDate}`}
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form noValidate>
                <Modal.Body className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {/* Title */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-2">
                      Title {!isEditingPastEvent && <span className="text-danger">*</span>}
                    </label>
                    <CustomFormikInput
                      disabled={loading || isSubmitting || isEditingPastEvent}
                      name="title"
                      placeholder="Enter event title"
                    />
                    <ErrorMessage name="title" component="div" className="text-danger small mt-1" />
                  </div>

                  {/* Event Type Checkboxes */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-2">Repeat</label>
                    <select
                      className="form-select"
                      value={values.repeat}
                      disabled={isEditingPastEvent}
                      onChange={(e) => handleRepeatChange(e.target.value as "once" | "weekly" | "monthly", setFieldValue, values.start_date)}
                    >
                      {REPEAT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {values.repeat === "weekly" && (
                      <small className="text-muted d-block mt-1">Repeats every week on this weekday at the selected South Africa time. Hourly, weekly, and monthly reminders fire before each occurrence.</small>
                    )}
                    {values.repeat === "monthly" && (
                      <small className="text-muted d-block mt-1">Repeats every month on this date at the selected South Africa time. Reminders fire before each occurrence.</small>
                    )}
                  </div>

                  {values.repeat === "once" && (
                  <div className="row mb-4 p-3 rounded-3 mx-1" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!values.is_multi_day}
                          onChange={() => {
                            setFieldValue("is_multi_day", false);
                            setFieldValue("end_date", values.start_date);
                          }}
                          disabled={isEditingPastEvent}
                        />
                        <label className="form-check-label fw-semibold">
                          <Sun size={16} className="me-1" /> Single Day Event
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={values.is_multi_day}
                          onChange={(e) => handleMultiDayToggle(e.target.checked, setFieldValue, values.start_date)}
                          disabled={isEditingPastEvent}
                        />
                        <label className="form-check-label fw-semibold">
                          <CalendarRange size={16} className="me-1" /> Multi-Day Event
                        </label>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Date & Time Fields */}
                  <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Times are in South Africa Standard Time (SAST, UTC+2)</small>
                    </div>
                    {/* Start Date */}
                    <div className="row g-2">
                      <div className="col-md-6">
                        <FormikDateInput
                          name="start_date"
                          label="Start Date"
                          min={minStartDate}
                          disabled={isEditingPastEvent}
                          onChange={(newDate) => {
                            if (values.end_date && newDate > values.end_date) {
                              setFieldValue("end_date", newDate);
                            }
                            if (!values.is_all_day) {
                              handleStartDateTimeChange(setFieldValue, newDate, values.start_time, values.repeat);
                            }
                          }}
                        />
                      </div>
                      {!values.is_all_day && (
                        <div className="col-md-3">
                          <FormikTimeInput
                            name="start_time"
                            label="Start Time (SAST)"
                            min={getMinTime(values.start_date)}
                            disabled={isEditingPastEvent}
                            onChange={(next) => handleStartDateTimeChange(setFieldValue, values.start_date, next, values.repeat)}
                          />
                        </div>
                      )}
                      {/* End Date/Time */}
                      {(values.is_multi_day || values.repeat !== "once") && (
                        <>
                          <div className="col-md-6">
                            <FormikDateInput
                              name="end_date"
                              label={values.repeat === "once" ? "End Date" : "Repeat until"}
                              min={getMinEndDate(values.start_date)}
                              disabled={isEditingPastEvent}
                            />
                          </div>
                          {!values.is_all_day && (
                            <div className="col-md-3">
                              <FormikTimeInput
                                name="end_time"
                                label="End Time (SAST)"
                                min={getMinTime(values.end_date)}
                                disabled={isEditingPastEvent}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {!values.is_multi_day && !values.is_all_day && values.repeat === "once" && (
                        <div className="col-md-3">
                          <FormikTimeInput
                            name="end_time"
                            label="End Time (SAST)"
                            min={getMinTime(values.end_date)}
                            disabled={isEditingPastEvent}
                          />
                        </div>
                      )}
                    </div>

                    {/* Info Messages */}
                    {values.is_all_day && values.is_multi_day &&
                      renderInfoMessage(
                        <CalendarRange size={14} />,
                        "Multi-day all-day event spans from 12:00 AM on start date to 11:59 PM on end date.",
                        "#e0f2fe"
                      )}
                    {values.is_all_day && !values.is_multi_day &&
                      renderInfoMessage(
                        <Sun size={14} />,
                        "All-day events span from 12:00 AM to 11:59 PM on the selected date."
                      )}
                  </div>

                  {/* Category & Mode */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold mb-2">
                        Category {!isEditingPastEvent && <span className="text-danger">*</span>}
                      </label>
                      <CustomFormikSelect
                        name="category"
                        options={categoryOptions}
                        disabled={loading || isSubmitting || isEditingPastEvent}
                      />
                      <ErrorMessage name="category" component="div" className="text-danger small mt-1" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold mb-2">
                        Event Mode {!isEditingPastEvent && <span className="text-danger">*</span>}
                      </label>
                      <CustomFormikSelect
                        name="mode"
                        options={MODE_OPTIONS}
                        disabled={loading || isSubmitting || isEditingPastEvent}
                      />
                      <ErrorMessage name="mode" component="div" className="text-danger small mt-1" />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold mb-2">
                        {values.mode === "online" ? "Link" : "Location"} {!isEditingPastEvent && <span className="text-danger">*</span>}
                      </label>
                      <CustomFormikInput
                        name="place_or_link"
                        placeholder={values.mode === "online" ? "Enter meeting link" : "Enter location"}
                        disabled={loading || isSubmitting || isEditingPastEvent}
                      />
                      <ErrorMessage name="place_or_link" component="div" className="text-danger small mt-1" />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-2">Description</label>
                    <CustomFormikInput
                      type="textarea"
                      name="description"
                      rows={3}
                      placeholder="Enter event description (optional)"
                      disabled={loading || isSubmitting || isEditingPastEvent}
                    />
                  </div>

                  {/* Invites */}
                  <InviteFieldArray
                    name="invites"
                    userOptions={userOptions}
                    values={values.invites}
                    disabled={isEditingPastEvent}
                    onUserSelect={(option, index) => handleUserSelect(option, index, values, setFieldValue)}
                  />

                  {/* Reminders */}
                  <ReminderFieldArray
                    name="reminders"
                    values={values.reminders}
                    disabled={isEditingPastEvent}
                  />

                  {/* Info Messages */}
                  {!isEditingPastEvent && !values.is_all_day && renderInfoMessage(
                    <Clock size={14} />,
                    `Current South Africa time is ${getSaNowLabel()}.`
                  )}
                  {isEditingPastEvent && renderInfoMessage(
                    <AlertCircle size={14} />,
                    "This event is in the past and cannot be edited. You can only view its details.",
                    "#fff3cd"
                  )}
                </Modal.Body>

                <Modal.Footer>
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4"
                      onClick={onHide}
                      disabled={isSubmitting}
                    >
                      {isEditingPastEvent ? "Close" : "Cancel"}
                    </button>
                    {!isEditingPastEvent && (
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={loading || isSubmitting}
                      >
                        {isSubmitting ? (
                          <><span className="spinner-border spinner-border-sm me-2" /> Saving...</>
                        ) : isUpdateMode ? "Update Event" : "Create Event"}
                      </button>
                    )}
                  </div>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default AddorUpdateModal;
